/**
 * Componente de linha para exibição de campanha em formato lista
 * Versão compacta do CampaignCard para visualização em tabela
 */

import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  Play,
  Pause,
  XCircle,
  Copy,
  Trash2,
  MoreVertical,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Users,
  CheckCheck,
  MessageSquare,
  Pencil,
  ShieldCheck,
} from 'lucide-react';
import { CampaignRecipientStats } from './CampaignCard';
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

// Interface da campanha
interface Campaign {
  id: string;
  name: string;
  description: string | null;
  channel_type: string;
  message_content: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  visibility?: EntityVisibility | null;
}

interface CampaignListRowProps {
  campaign: Campaign;
  recipientStats?: CampaignRecipientStats;
  canEdit: boolean;
  canCreate: boolean;
  canDelete: boolean;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onEdit: () => void;
  /** Abre o diálogo de gerenciamento de visibilidade. */
  onManageVisibility?: () => void;
}

// Configuração de status com cores
const statusConfig: Record<string, {
  label: string;
  icon: any;
  badgeClass: string;
}> = {
  draft: {
    label: 'Rascunho',
    icon: Clock,
    badgeClass: 'bg-muted text-muted-foreground border-muted-foreground/30',
  },
  scheduled: {
    label: 'Agendada',
    icon: Clock,
    badgeClass: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  },
  running: {
    label: 'Em execução',
    icon: Play,
    badgeClass: 'bg-primary/10 text-primary border-primary/30',
  },
  paused: {
    label: 'Pausada',
    icon: Pause,
    badgeClass: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  },
  completed: {
    label: 'Finalizada',
    icon: CheckCircle2,
    badgeClass: 'bg-green-500/10 text-green-600 border-green-500/30',
  },
  cancelled: {
    label: 'Cancelada',
    icon: XCircle,
    badgeClass: 'bg-destructive/10 text-destructive border-destructive/30',
  },
  failed: {
    label: 'Falha',
    icon: AlertCircle,
    badgeClass: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};

// Labels de canal
const channelLabels: Record<string, string> = {
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  telegram: 'Telegram',
  webchat: 'WebChat',
};

export function CampaignListRow({
  campaign,
  recipientStats,
  canEdit,
  canCreate,
  canDelete,
  onStart,
  onPause,
  onResume,
  onCancel,
  onDuplicate,
  onDelete,
  onEdit,
  onManageVisibility,
}: CampaignListRowProps) {
  const navigate = useNavigate();
  const status = statusConfig[campaign.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  // Usa estatísticas passadas via props ou fallback para cálculo local
  const stats = recipientStats || {
    total: campaign.total_recipients,
    pending: Math.max(0, campaign.total_recipients - campaign.sent_count - campaign.failed_count),
    sent: Math.max(0, campaign.sent_count - campaign.delivered_count),
    delivered: Math.max(0, campaign.delivered_count - campaign.read_count),
    read: campaign.read_count,
    failed: campaign.failed_count,
    skipped: 0,
  };

  // Calcula progresso
  const processedCount = stats.sent + stats.delivered + stats.read + stats.failed + stats.skipped;
  const progress = stats.total > 0 ? Math.round((processedCount / stats.total) * 100) : 0;

  // Formata data compacta
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors group">
      {/* Ícone de status */}
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
        campaign.status === 'completed' && 'bg-green-500/10',
        campaign.status === 'running' && 'bg-primary/10',
        campaign.status === 'paused' && 'bg-yellow-500/10',
        campaign.status === 'scheduled' && 'bg-blue-500/10',
        campaign.status === 'cancelled' && 'bg-destructive/10',
        campaign.status === 'failed' && 'bg-destructive/10',
        campaign.status === 'draft' && 'bg-muted',
      )}>
        <StatusIcon className={cn(
          'h-4 w-4',
          campaign.status === 'completed' && 'text-green-600',
          campaign.status === 'running' && 'text-primary animate-pulse',
          campaign.status === 'paused' && 'text-yellow-600',
          campaign.status === 'scheduled' && 'text-blue-600',
          campaign.status === 'cancelled' && 'text-destructive',
          campaign.status === 'failed' && 'text-destructive',
          campaign.status === 'draft' && 'text-muted-foreground',
        )} />
      </div>

      {/* Nome e descrição */}
      <div className="flex-1 min-w-0">
        <p 
          className="font-medium truncate cursor-pointer hover:text-primary transition-colors"
          onClick={() => navigate(`/tenant/campaigns/${campaign.id}`)}
        >
          {campaign.name}
        </p>
        {campaign.description && (
          <p className="text-sm text-muted-foreground truncate max-w-[300px]">
            {campaign.description}
          </p>
        )}
      </div>

      {/* Badge de canal */}
      <Badge variant="outline" className="font-normal text-xs shrink-0">
        <MessageSquare className="h-3 w-3 mr-1" />
        {channelLabels[campaign.channel_type] || campaign.channel_type}
      </Badge>

      {/* Badge de visibilidade */}
      <VisibilityBadge visibility={campaign.visibility ?? 'public'} className="shrink-0" />


      {/* Barra de progresso compacta */}
      <div className="w-24 shrink-0 flex items-center gap-2">
        <Progress value={progress} className="h-2 flex-1" />
        <span className="text-xs text-muted-foreground w-8">{progress}%</span>
      </div>

      {/* Estatísticas compactas */}
      <div className="flex items-center gap-3 text-xs shrink-0">
        <div className="flex items-center gap-1" title="Total">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{stats.total}</span>
        </div>
        <div className="flex items-center gap-1" title="Entregues">
          <CheckCheck className="h-3.5 w-3.5 text-green-500" />
          <span className="font-medium text-green-600">{stats.delivered}</span>
        </div>
        <div className="flex items-center gap-1" title="Lidos">
          <Eye className="h-3.5 w-3.5 text-primary" />
          <span className="font-medium text-primary">{stats.read}</span>
        </div>
        <div className="flex items-center gap-1" title="Falhas">
          <XCircle className="h-3.5 w-3.5 text-destructive" />
          <span className="font-medium text-destructive">{stats.failed}</span>
        </div>
      </div>

      {/* Data */}
      <span className="text-xs text-muted-foreground w-20 shrink-0 text-right">
        {formatDate(campaign.created_at)}
      </span>

      {/* Menu de ações */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => navigate(`/tenant/campaigns/${campaign.id}`)}>
            <Eye className="h-4 w-4 mr-2" />
            Visualizar
          </DropdownMenuItem>

          {onManageVisibility && (
            <DropdownMenuItem onClick={onManageVisibility}>
              <ShieldCheck className="h-4 w-4 mr-2" />
              Visibilidade
            </DropdownMenuItem>
          )}

          {['draft', 'scheduled'].includes(campaign.status) && canEdit && (
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
          )}

          {campaign.status === 'draft' && canEdit && (
            <DropdownMenuItem onClick={onStart}>
              <Play className="h-4 w-4 mr-2" />
              Iniciar
            </DropdownMenuItem>
          )}

          {campaign.status === 'running' && canEdit && (
            <DropdownMenuItem onClick={onPause}>
              <Pause className="h-4 w-4 mr-2" />
              Pausar
            </DropdownMenuItem>
          )}

          {campaign.status === 'paused' && canEdit && (
            <DropdownMenuItem onClick={onResume}>
              <Play className="h-4 w-4 mr-2" />
              Retomar
            </DropdownMenuItem>
          )}

          {['running', 'paused'].includes(campaign.status) && canEdit && (
            <DropdownMenuItem onClick={onCancel} className="text-destructive">
              <XCircle className="h-4 w-4 mr-2" />
              Cancelar
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {canCreate && (
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicar
            </DropdownMenuItem>
          )}

          {campaign.status === 'draft' && canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
